// ─────────────────────────────────────────────────────────────────────────────
// Client entry. Replaces the previous main.tsx — index.html now points here.
//
// Responsibilities:
//
//   1. Run the bootstrap side effects (telemetry fetch guard) BEFORE any
//      third-party SDK can capture window.fetch.
//   2. Initialise PostHog and BetterStack when their tokens are present.
//   3. Read SSR-prefetched data from window.__VELLUM_INITIAL_DATA__ and feed
//      it into the React tree via SSRProvider so pages can consume it on
//      first render without a network round-trip.
//   4. Mount the React tree via createRoot (never hydrateRoot — see the
//      block comment on the mount site below for why).
//
// All of (1)–(3) above are browser-only and would fail in Node, which is
// why this file must NEVER be imported by entry-server.tsx. See
// IMPLEMENTATION_NOTES.md for the rationale on the two-entry split.
// ─────────────────────────────────────────────────────────────────────────────

// Bootstrap MUST be the first import. It installs the telemetry fetch guard
// before posthog-js or @logtail/browser have a chance to capture a reference
// to the unwrapped window.fetch. See bootstrap.ts for why this has to live
// in a separate file rather than inline here.
import './bootstrap';

import React from 'react';
import ReactDOM from 'react-dom/client';
import posthog from 'posthog-js';
import { Logtail } from '@logtail/browser';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { SSRProvider, type SSRInitialData } from './ssr/SSRContext';

// ── PostHog analytics ─────────────────────────────────────────────────────────
// VITE_POSTHOG_KEY is injected at build time. Omit in local dev for a no-op.
//
// Consent: we initialise PostHog with `opt_out_capturing_by_default: true` so
// nothing is sent until the user opts in via the cookie consent banner.
// CookieConsentContext calls posthog.opt_in_capturing() when the user
// consents, and posthog.opt_out_capturing() when they withdraw.
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string) ?? 'https://us.i.posthog.com',
    defaults: '2026-01-30',
    // Only build person profiles for identified (logged-in) users.
    // Anonymous visitors are tracked in aggregate — avoids unnecessary billing
    // and keeps PII surface area small.
    person_profiles: 'identified_only',
    // Disable auto pageview — React Router's client-side navigation is
    // invisible to PostHog's default detection. PostHogPageView handles this.
    capture_pageview: false,
    capture_pageleave: true,
    // Start opted-out. CookieConsentContext will call opt_in_capturing()
    // once the user explicitly consents to analytics cookies.
    opt_out_capturing_by_default: true,
    // Respect Global Privacy Control as an implicit opt-out.
    respect_dnt: false, // we do not act on DNT (no consensus); GPC is handled in CookieConsentContext
  });
}

// ── BetterStack error tracking ────────────────────────────────────────────────
// VITE_BETTERSTACK_TOKEN is injected at build time. No-op when absent.
//
// Note: error tracking is operated on the legitimate-interests basis for
// security and debugging. It does not require analytics consent. If you
// prefer to treat it as analytics, gate this block on `hasAnalyticsConsent()`
// from CookieConsentContext.
export const logtail = import.meta.env.VITE_BETTERSTACK_TOKEN
  ? new Logtail(import.meta.env.VITE_BETTERSTACK_TOKEN as string)
  : null;

// ── Telemetry noise safety net ────────────────────────────────────────────────
// The fetch guard above catches telemetry errors at the source, but we keep
// a lightweight unhandledrejection filter as belt-and-braces. It matches on
// error MESSAGE only (not stack) because stack-based matching is unreliable
// in production — see telemetryFetchGuard.ts for the full rationale.
//
// Matching just "Failed to fetch" is safe here because our own API calls are
// wrapped by axios with interceptors that always attach a .catch() — an
// unhandled rejection with this exact message is, in practice, a fire-and-
// forget third-party SDK call and not a bug we care about.
function isLikelyBlockedTelemetry(reason: unknown): boolean {
  if (!(reason instanceof TypeError)) return false;
  return reason.message === 'Failed to fetch';
}

// Capture phase so this runs before any other listener registered later,
// including logtail's own listener below.
window.addEventListener(
  'unhandledrejection',
  (event) => {
    if (isLikelyBlockedTelemetry(event.reason)) {
      event.preventDefault(); // silences the "Uncaught (in promise)" console log
    }
  },
  true,
);

if (logtail) {
  // Catch unhandled promise rejections (e.g. failed API calls with no .catch())
  window.addEventListener('unhandledrejection', (event) => {
    // Skip telemetry-origin errors — they're blocked by ad blockers, not bugs.
    if (isLikelyBlockedTelemetry(event.reason)) return;
    logtail.error('Unhandled promise rejection', {
      reason: String(event.reason),
      stack: event.reason?.stack,
    });
  });

  // Catch synchronous JS errors that bubble to window (outside React tree)
  window.addEventListener('error', (event) => {
    logtail.error(event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
    });
  });
}

// ── SSR initial data pickup ──────────────────────────────────────────────────
//
// The server serialises its loader output into a <script> tag that runs
// before this bundle loads. Read it, then null out the global so it doesn't
// leak across client-side route changes.
//
// Type safety: we trust the server to have produced a value matching
// SSRInitialData. Malicious injection would have to go through our own
// server's safeJson escaper in server.js, which covers </script> and the
// U+2028/U+2029 line terminator cases. XSS vector is closed.
declare global {
  interface Window {
    __VELLUM_INITIAL_DATA__?: SSRInitialData;
  }
}
const initialData: SSRInitialData | null = window.__VELLUM_INITIAL_DATA__ ?? null;
if (window.__VELLUM_INITIAL_DATA__) {
  // Break the reference so subsequent client-side navigation to a page
  // that calls useInitialSSRData doesn't get stale SSR data. The context
  // value is captured once at mount time, so this is safe.
  delete window.__VELLUM_INITIAL_DATA__;
}

// ── App ───────────────────────────────────────────────────────────────────────
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in DOM');

/**
 * Mount strategy: always createRoot, never hydrateRoot.
 *
 * The server-side rendered HTML (from entry-server.tsx) and the build-time
 * prerendered HTML (from scripts/prerender.mjs) exist for two reasons:
 * crawlers get meaningful content without executing JS, and real users
 * see the above-the-fold content during the brief window between HTML
 * parse and JS boot. Neither use case requires the client to hydrate onto
 * the existing DOM.
 *
 * We originally tried hydrateRoot here. It triggered React errors #418
 * ("text content does not match server-rendered HTML") and #423
 * ("error while hydrating, entire root will switch to client rendering")
 * because the server's provider tree is deliberately smaller than the
 * client's — the server omits Auth/Cookie/WebSocket providers (they have
 * side effects that don't belong on the render path), while the client
 * wraps AppRoutes in the full <App /> stack. Even if those providers
 * didn't differ, user-specific state (auth cache, locale from
 * localStorage, GPC signal, consent banner state) varies between a fresh
 * server render and whatever the user's browser has cached — so the
 * mismatch is fundamentally unavoidable without a major refactor.
 *
 * React's hydration failure was not silent; it logged two errors per
 * page load and then discarded the server HTML and re-rendered the whole
 * tree anyway. Switching to createRoot unconditionally:
 *
 *   - Eliminates the console noise (no more #418/#423 every page load).
 *   - Produces the same visual result — React discards the server HTML
 *     and re-renders either way; the only difference is that with
 *     createRoot we skip the doomed hydration attempt.
 *   - Preserves all the SEO / initial-paint benefits: crawlers still get
 *     the server-rendered HTML, and users still see the prerendered
 *     content during JS boot. Nothing changes for either audience.
 *
 * If a future refactor aligns the server + client provider trees (and
 * makes all providers SSR-safe), we can revisit this and switch back to
 * hydrateRoot to reclaim the "no re-render cost" benefit. Until then,
 * createRoot is correct.
 */
const tree = (
  <React.StrictMode>
    {/* ErrorBoundary catches React render-time errors and ships them to
        BetterStack via the onError prop. It is a transparent passthrough
        when logtail is null (i.e. no token set in local dev). */}
    <ErrorBoundary
      onError={(error, info) => {
        logtail?.error(error.message, {
          stack: error.stack,
          componentStack: info.componentStack,
        });
      }}
    >
      <SSRProvider value={{ initialData, isServer: false }}>
        <App />
      </SSRProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Always createRoot. See the block comment above for why hydrateRoot was
// removed.
ReactDOM.createRoot(rootEl).render(tree);
